/**
 * Quill LanguageTool Plugin
 *
 * This is a converted version of quill-languagetool.
 * Original source: https://github.com/vantezzen/quill-languagetool
 */

/**
   * Manages the suggestion popups that appear when you click on a match.
   */

class PopupManager {
  constructor(parent) {
    this.parent = parent;
    this.quill = parent.quill;
    this.currentPopup = null;
  }

  closePopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  createPopup(match, box) {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }

    // Main popup container
    const popup = document.createElement('div');
    popup.classList.add('quill-lt-match-popup');
    popup.style.position = 'absolute';
    popup.style.visibility = 'hidden';

    // Close button
    const closeButton = document.createElement('button');
    closeButton.classList.add('quill-lt-match-popup-close');
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => popup.remove());
    popup.appendChild(closeButton);

    // Title
    const title = document.createElement('div');
    title.classList.add('quill-lt-match-popup-title');
    title.innerText = match.shortMessage || 'Suggestion';
    popup.appendChild(title);

    // Description (full message)
    const description = document.createElement('div');
    description.classList.add('quill-lt-match-popup-description');
    description.innerText = match.message;
    popup.appendChild(description);

    // Actions (suggestions)
    const actions = document.createElement('div');
    actions.classList.add('quill-lt-match-popup-actions');
    match.replacements.forEach(replacement => {
      const suggestion = document.createElement('button');
      suggestion.classList.add('quill-lt-match-popup-action');
      suggestion.innerText = replacement.value;
      suggestion.addEventListener('click', () => {
        this.parent.preventLoop();

        const oldLength = match.length;
        const newLength = replacement.value.length;
        const lengthDelta = newLength - oldLength;
        const currentOffset = match.offset;

        // Correct the text in the editor
        this.quill.deleteText(currentOffset, oldLength, 'user');
        this.quill.insertText(currentOffset, replacement.value, 'user');
        this.quill.setSelection(currentOffset + newLength, 0, 'silent');
        popup.remove();

        // 1. Remove the corrected match from the array.
        this.parent.matches = this.parent.matches.filter(m => m !== match);

        // 2. Update the offsets of all subsequent matches.
        this.parent.matches.forEach(m => {
          if (m.offset > currentOffset) {
            m.offset += lengthDelta;
          }
        });

        // 3. Now, redraw the boxes with the updated match data.
        this.parent.reloadBoxes();
      });
      actions.appendChild(suggestion);
    });
    popup.appendChild(actions);

    // Append to container to measure dimensions
    this.quill.container.appendChild(popup);
    this.currentPopup = popup;

    // Get dimensions for positioning
    const boxRect = box.getBoundingClientRect();
    const quillRect = this.quill.container.getBoundingClientRect();
    const popupHeight = popup.offsetHeight;
    const margin = 8; // A small gap above the text

    // Set final position
    popup.style.left = `${boxRect.left - quillRect.left}px`;
    popup.style.top = `${boxRect.top - quillRect.top - popupHeight - margin}px`;

    // Make the popup visible now that it is positioned
    popup.style.visibility = 'visible';

    document.addEventListener('click', (e) => {
      if (!popup.contains(e.target) && e.target !== box) {
        popup.remove();
        this.currentPopup = null;
      }
    }, { once: true });
  }
}

/**
   * Manages the suggestion boxes (the underlines for matches).
   */
class SuggestionBoxes {
  constructor(parent) {
    this.parent = parent;
  }

  addSuggestionBoxes() {
    this.parent.matches.forEach(match => {
      this.parent.quill.formatText(match.offset, match.length, 'ltmatch', match.rule.id, 'silent');
    });

    const blots = document.querySelectorAll('.quill-lt-match');
    blots.forEach(blot => {
      blot.addEventListener('click', (e) => {
        const ruleId = blot.dataset.rule;
        const Quill = this.parent.quill.constructor;
        const blotInstance = Quill.find(blot);
        if (!blotInstance) {
          return;
        }

        const blotOffset = blotInstance.offset(this.parent.quill.scroll);
        const match = this.parent.matches.find(m => m.rule.id === ruleId && m.offset === blotOffset);
        if (match) {
          this.parent.popups.createPopup(match, blot);
        }
        e.stopPropagation();
      });
    });
  }

  removeSuggestionBoxes() {
    this.parent.quill.formatText(0, this.parent.quill.getLength(), 'ltmatch', false, 'silent');
  }
}

/**
   * Creates and manages the loading indicator.
   */
class LoadingIndicator {
  constructor(parent) {
    this.parent = parent;
    this.quill = parent.quill;
    this.element = null;
  }

  create() {
    if (this.element || !this.parent.params.showLoadingIndicator) {
      return;
    }
    this.element = document.createElement('div');
    this.element.classList.add('quill-lt-loading-indicator');

    const spinner = document.createElement('div');
    spinner.classList.add('quill-lt-loading-indicator-spinner');
    this.element.appendChild(spinner);

    this.quill.container.appendChild(this.element);
  }

  startLoading() {
    this.create();
    if (this.element) {
      this.element.style.display = 'block';
    }
  }

  stopLoading() {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }
}

/**
   * Creates the suggestion blot (the inline element for matches).
   */
function createSuggestionBlotForQuillInstance(Quill) {
  const Inline = Quill.import('blots/inline');
  class SuggestionBlot extends Inline {
    static create(value) {
      const node = super.create(value);
      node.setAttribute('data-rule', value);
      return node;
    }

    static formats(domNode) {
      return domNode.getAttribute('data-rule');
    }
  }
  SuggestionBlot.blotName = 'ltmatch';
  SuggestionBlot.tagName = 'quill-lt-match';
  SuggestionBlot.className = 'quill-lt-match';
  return SuggestionBlot;
}

// --- Main Plugin Class ---

class QuillLanguageTool {
  static DEFAULTS = {
    language: 'en-US',
    disableNativeSpellcheck: true,
    cooldownTime: 3000,
    showLoadingIndicator: true,
    apiOptions: {},
    spellCheck: null,
    orgId: null
  };

  constructor(quill, params) {
    this.quill = quill;
    this.params = { ...QuillLanguageTool.DEFAULTS, ...params };

    this.boxes = new SuggestionBoxes(this);
    this.popups = new PopupManager(this);
    this.loader = new LoadingIndicator(this);
    this.matches = [];
    this.typingCooldown = undefined;
    this.loopPreventionCooldown = undefined;

    this.quill.on('text-change', (delta, _oldDelta, source) => {
      if (source === 'user') {

        this.popups.closePopup();
        // Before we run the onTextChange check if the new characters added are added next to a match
        // if yes then break the format to prevent stale formatting or applying the format directly to the new characters
        // let the spell checker decide each time
        let currentIndex = 0;
        const ops = delta.ops || [];

        ops.forEach(op => {
          if (op.retain) {
            currentIndex += op.retain;
          } else if (op.insert) {
            const insertionLength = op.insert.length;
            const relevantMatch = this.matches.find(match =>
              currentIndex === match.offset || currentIndex === match.offset + match.length
            );
            if (relevantMatch) {
              this.quill.formatText(currentIndex, insertionLength, 'ltmatch', false, 'silent');
            }

            currentIndex += insertionLength;
          }
        });

        this.onTextChange();
      }
    });

    this.checkSpelling();
    this.disableNativeSpellcheckIfSet();
  }

  disableNativeSpellcheckIfSet() {
    if (this.params.disableNativeSpellcheck) {
      this.quill.root.setAttribute('spellcheck', 'false');
    }
  }

  onTextChange() {
    if (this.loopPreventionCooldown) {
      return;
    }

    // Check if the editor is empty
    // If it's empty remove any stale errors and formatting
    // and prevent any triggered calls
    const text = this.quill.getText();
    if (text.trim() === '') {
      this.matches = [];
      this.boxes.removeSuggestionBoxes();
      if (this.typingCooldown) {
        clearTimeout(this.typingCooldown);
        this.typingCooldown = undefined;
      }
      this.loader.stopLoading();
      return;
    }

    if (this.typingCooldown) {
      clearTimeout(this.typingCooldown);
    }
    this.typingCooldown = setTimeout(() => {
      this.checkSpelling();
    }, this.params.cooldownTime);
  }

  async reloadBoxes() {
    this.boxes.removeSuggestionBoxes();
    this.boxes.addSuggestionBoxes();
  }

  async checkSpelling() {
    this.boxes.removeSuggestionBoxes();

    if (document.querySelector('lt-toolbar')) {
      return;
    }

    this.loader.startLoading();
    const json = await this.getLanguageToolResults();
    this.popups.closePopup();

    if (json && json.matches) {
      this.matches = json.matches.map(match => ({ ...match }));
      this.boxes.addSuggestionBoxes();
    } else {
      this.matches = [];
    }
    this.loader.stopLoading();
  }

  async getLanguageToolResults() {
    const { spellCheck, orgId } = this.params;

    if (!spellCheck || !orgId) {
      console.error('QuillLanguageTool: `spellCheck` function and `orgId` must be provided in params.');
      return null;
    }

    const data = this.getApiData();

    if (!data) {
      return null;
    }

    try {
      const result = await spellCheck({ orgId, data });

      if (result.error) {
        throw result.error;
      }
      return result.data;
    } catch (e) {
      console.error('Error checking spelling:', e);
      return null;
    }
  }

  getApiData() {
    const dataObject = {
      text: this.quill.getText(),
      language: this.params.language,
      ...this.params.apiOptions
    };

    if (!this.quill.getText()?.trim()) {
      return null;
    }

    return dataObject;
  }

  preventLoop() {
    if (this.loopPreventionCooldown) {
      clearTimeout(this.loopPreventionCooldown);
    }
    this.loopPreventionCooldown = setTimeout(() => {
      this.loopPreventionCooldown = undefined;
    }, 100);
  }
}

/**
   * Register all QuillLanguageTool modules with Quill.
   */
export default function registerQuillLanguageTool(Quill) {
  Quill.register({
    'modules/languageTool': QuillLanguageTool,
    'formats/ltmatch': createSuggestionBlotForQuillInstance(Quill)
  });
}
